import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { Aparece, EASE_SALIDA, useSinAnimacion } from "./Aparece";
import { Boton } from "./Boton";
import { CIFRAS, PASOS_MES } from "./content";
import { MarcaDeAgua } from "./MarcaDeAgua";
import { MockupPaso } from "./mockups/MockupPaso";

// "Tu mes, resuelto": la única franja oscura de la página, y el único lugar donde el dorado es
// texto (sobre indigo-950 sí tiene contraste). En desktop el mockup queda fijo con `position:
// sticky` NATIVO y sólo cambia de estado según el paso que se lee — no se secuestra el scroll. En
// mobile cada paso trae su propio mockup. Todo el texto está siempre en la página: si el observer
// no dispara, el mockup se queda en el paso 1 y no se pierde nada.

const DiaDorado = ({ dia }) => (
  <span className="bg-gradient-to-r from-gold-700 via-gold-400 to-gold-600 bg-clip-text font-display text-5xl font-extrabold tabular-nums tracking-tight text-transparent sm:text-6xl">
    {dia}
  </span>
);

const Chips = ({ chips }) => (
  <ul className="mt-5 flex flex-wrap gap-2">
    {chips.map((c) => (
      <li
        key={c.label}
        className="rounded-full bg-white/5 px-3 py-1 text-xs font-semibold text-indigo-100 ring-1 ring-inset ring-white/10"
      >
        {c.label}
      </li>
    ))}
  </ul>
);

// Franja de cifras: lo PRIMERO que se ve cuando esta capa termina de tapar el hero fijo, que es el
// momento de más atención de la página. Tres números que el producto sostiene (ver `CIFRAS`), en
// el dorado de la marca: sobre indigo-950 es el único fondo donde el dorado tiene contraste de texto.
const FranjaCifras = () => (
  <div className="border-b border-white/10 pb-16 lg:pb-20" data-testid="franja-cifras">
    <Aparece>
      <p className="font-display text-2xl font-extrabold tracking-tight sm:text-3xl">{CIFRAS.titulo}</p>
    </Aparece>
    <dl className="mt-10 grid gap-10 sm:grid-cols-3 sm:gap-0 sm:divide-x sm:divide-white/10">
      {CIFRAS.items.map((c, i) => (
        <Aparece key={c.titulo} delay={i * 0.06} className="sm:px-8 sm:first:pl-0 sm:last:pr-0">
          {/* El "%" va pegado a la cifra; "clic" y "planillas" son palabras y llevan espacio. */}
          <dt className={cn("flex items-baseline", c.unidad === "%" ? "gap-0.5" : "gap-2")}>
            <span className="bg-gradient-to-r from-gold-700 via-gold-400 to-gold-600 bg-clip-text font-display text-6xl font-extrabold tabular-nums tracking-tight text-transparent sm:text-7xl">
              {c.cifra}
            </span>
            <span className={cn("font-display font-extrabold text-gold-500", c.unidad === "%" ? "text-4xl sm:text-5xl" : "text-xl")}>{c.unidad}</span>
          </dt>
          <dd className="mt-3">
            <p className="text-base font-bold text-white">{c.titulo}</p>
            <p className="mt-1 text-sm leading-relaxed text-indigo-200/80">{c.texto}</p>
          </dd>
        </Aparece>
      ))}
    </dl>
  </div>
);

export const MesResuelto = () => {
  const [activo, setActivo] = useState(0);
  const refs = useRef([]);
  const sinAnimacion = useSinAnimacion();

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return undefined;
    // Una franja angosta a media pantalla: el paso que la cruza es el que se está leyendo.
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActivo(Number(e.target.dataset.indice));
        });
      },
      { rootMargin: "-45% 0px -50% 0px" },
    );
    refs.current.forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const irAPaso = (i) => {
    refs.current[i]?.scrollIntoView({ behavior: sinAnimacion ? "auto" : "smooth", block: "center" });
  };

  const pasoActivo = PASOS_MES[activo];

  return (
    // Es la capa que sube sobre el hero fijo (`LandingPage`): plana, sin esquinas ni sombra, como
    // xpendit.com — el usuario probó la versión con profundidad y la descartó.
    // ⚠️ Sin `overflow-hidden`: convertiría la sección en contenedor de scroll y el mockup sticky
    // de adentro dejaría de quedar fijo.
    <section
      id="como-funciona"
      className="relative scroll-mt-16 bg-indigo-950 text-white"
      data-testid="landing-mes"
    >
      <MarcaDeAgua className="h-56 text-white opacity-[0.06] sm:h-72" />
      <div className="relative mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:py-28">
        <FranjaCifras />
        <Aparece className="mt-16 max-w-2xl lg:mt-20">
          <p className="text-xs font-semibold uppercase tracking-widest text-indigo-300">Cómo funciona</p>
          <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
            Tu mes, resuelto. <span className="text-indigo-300">Del primer cobro a la última liquidación.</span>
          </h2>
          <p className="mt-4 text-base leading-relaxed text-indigo-200/80">
            Así se ve un mes de administración de arriendos, antes y con SEBBIA.
          </p>
        </Aparece>

        <div className="mt-14 grid gap-12 lg:grid-cols-[1fr_1.1fr] lg:gap-16">
          <ol className="space-y-16 lg:space-y-0">
            {PASOS_MES.map((p, i) => (
              <li
                key={p.key}
                ref={(el) => { refs.current[i] = el; }}
                data-indice={i}
                className={cn(
                  "lg:flex lg:min-h-[62vh] lg:flex-col lg:justify-center",
                  !sinAnimacion && "transition-opacity duration-300 lg:opacity-50",
                  !sinAnimacion && activo === i && "lg:opacity-100",
                )}
                data-testid={`paso-${p.key}`}
              >
                <div className="flex items-baseline gap-4">
                  <p className="text-xs font-semibold uppercase tracking-widest text-indigo-300">Día</p>
                  <DiaDorado dia={p.dia} />
                </div>
                <h3 className="mt-2 font-display text-2xl font-extrabold tracking-tight">{p.titulo}</h3>
                <p className="mt-4 flex gap-2 text-sm leading-relaxed text-indigo-200/70">
                  <Clock className="mt-0.5 h-4 w-4 shrink-0 text-indigo-400" />
                  <span><span className="font-semibold text-indigo-200">Antes:</span> {p.antes}</span>
                </p>
                <p className="mt-3 text-base leading-relaxed text-white">{p.ahora}</p>
                <Chips chips={p.chips} />
                <div className="mt-8 lg:hidden">
                  <MockupPaso paso={p.key} />
                </div>
              </li>
            ))}
          </ol>

          <div className="hidden lg:block">
            <div className="sticky top-24">
              <div className="mb-5 flex gap-2" role="tablist" aria-label="Pasos del mes">
                {PASOS_MES.map((p, i) => (
                  <button
                    key={p.key}
                    type="button"
                    role="tab"
                    aria-selected={activo === i}
                    onClick={() => irAPaso(i)}
                    className={cn(
                      "cursor-pointer whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/20",
                      activo === i ? "bg-white text-indigo-950" : "text-indigo-200 hover:bg-white/10",
                    )}
                  >
                    Día {p.dia} · {p.corto}
                  </button>
                ))}
              </div>
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={pasoActivo.key}
                  initial={sinAnimacion ? false : { opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={sinAnimacion ? undefined : { opacity: 0, y: -10 }}
                  transition={{ duration: 0.28, ease: EASE_SALIDA }}
                >
                  <MockupPaso paso={pasoActivo.key} />
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>

        <Aparece className="mt-20 flex flex-col items-start gap-4 border-t border-white/10 pt-10 sm:flex-row sm:items-center sm:justify-between">
          <p className="font-display text-xl font-extrabold tracking-tight sm:text-2xl">
            ¿Así quieres tu próximo cierre de mes?
          </p>
          <Boton href="#demo" variante="oscuro" tamano="lg" data-testid="mes-demo">
            Agenda una demo <ArrowRight className="h-4 w-4" />
          </Boton>
        </Aparece>
      </div>
    </section>
  );
};
